<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  configuration_imports: { type: Array,  default: () => [] },
  labels:                { type: Object, default: () => ({}) },
  save_url:              { type: String, default: '' },
  back_url:              { type: String, default: '/administration' },
})

const { error: toastError } = useToast()

// Shape attendu par le controller : params[:configuration_imports][id][field]
// On n'envoie que les 2 champs éditables (les 2 autres sont readonly)
const initialPayload = {}
props.configuration_imports.forEach(c => {
  initialPayload[String(c.id)] = {
    import_file_column_name:      c.import_file_column_name ?? '',
    import_file_column_mandatory: !!c.import_file_column_mandatory,
  }
})

const form = useForm({
  configuration_imports: initialPayload,
})

function submit() {
  form.post(props.save_url, {
    preserveScroll: true,
    onError: (errs) => {
      const list = Object.values(errs).flat()
      if (list.length) list.forEach(m => toastError(m))
      else toastError('Erreur lors de la mise à jour.')
    },
  })
}

function cancel() {
  window.location.href = props.back_url
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration &rsaquo; Importation
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Configuration de l'importation
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ configuration_imports.length }} colonne{{ configuration_imports.length > 1 ? 's' : '' }} d'import
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Sous-navigation -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10 mb-6">
          <a href="/administration" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Liste</a>
          <a href="/administration/edit_config_client" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Client</a>
          <a href="/administration/edit_logos" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Logos</a>
          <a href="/administration/edit_config_bordereau" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Bordereau</a>
          <a href="/administration/edit_config_importation" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-primary bg-surface-container rounded-sm border-l-4 border-tertiary">Importation</a>
          <a href="/administration/edit_config_module" class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">Modules</a>
        </nav>

        <!-- Form Card -->
        <form @submit.prevent="submit" class="space-y-4">
          <section class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-outline-variant/10">
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.import_model_name || 'Modèle' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.import_model_column_name || 'Colonne BD' }}</th>
                    <th class="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">{{ labels.import_file_column_name || 'Colonne fichier' }}</th>
                    <th class="text-center px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-28">{{ labels.import_file_column_mandatory || 'Obligatoire' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="configuration_imports.length === 0">
                    <td colspan="4" class="px-6 py-6 text-center text-sm text-outline/60">
                      Aucune configuration d'importation.
                    </td>
                  </tr>
                  <tr
                    v-for="ci in configuration_imports"
                    :key="ci.id"
                    class="border-b border-outline-variant/5 hover:bg-surface-container-high/20 transition-colors"
                  >
                    <!-- Readonly : Modèle -->
                    <td class="px-4 py-3 font-bold text-on-surface">
                      {{ ci.import_model_name || '—' }}
                    </td>
                    <!-- Readonly : Colonne BD -->
                    <td class="px-4 py-3 font-mono text-outline">
                      {{ ci.import_model_column_name || '—' }}
                    </td>
                    <!-- Éditable : Colonne fichier -->
                    <td class="px-4 py-2">
                      <input
                        v-model="form.configuration_imports[String(ci.id)].import_file_column_name"
                        type="text"
                        class="w-full bg-surface-container-highest border-none rounded-sm text-on-surface placeholder:text-outline/50 px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                      />
                    </td>
                    <!-- Éditable : Obligatoire (toggle) -->
                    <td class="px-4 py-2 w-28 text-center">
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input
                          v-model="form.configuration_imports[String(ci.id)].import_file_column_mandatory"
                          type="checkbox"
                          class="sr-only peer"
                        />
                        <div class="w-10 h-5 bg-surface-container-highest rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-on-surface after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-5 peer-checked:after:bg-on-primary"></div>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              @click="cancel"
              class="px-6 py-3 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="form.processing"
              class="px-8 py-3 machined-sheen text-on-primary font-black rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
            >
              <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-base">save</span>
              Enregistrer
            </button>
          </div>
        </form>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
